import { Table } from "flowbite-react";
import {
  formatDocument,
  formatToReal,
  getImageURLScore,
} from "../../../utils/utils";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import { HiClipboardList } from "react-icons/hi";
import { HiBuildingLibrary, HiCalendarDays } from "react-icons/hi2";

function HighlightStat({ icon = "HiClipboardList", label, value, hint }) {
  const icons = {
    HiClipboardList: HiClipboardList,
    HiBuildingLibrary: HiBuildingLibrary,
    HiCalendarDays: HiCalendarDays,
  };
  const IconCmp = icons[icon] || HiClipboardList;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
      {/* Glow suave */}
      <div
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 hover:opacity-100 transition-opacity"
        style={{
          background:
            "radial-gradient(600px circle at var(--x,50%) var(--y,50%), rgba(59,130,246,0.08), transparent 40%)",
        }}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
          e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
        }}
      />
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50">
          <IconCmp className="h-6 w-6 text-blue-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wide text-gray-500">
            {label}
          </div>
          <div className=" text-xl font-semibold text-gray-900 leading-tight">
            {value ?? "-"}
          </div>
        </div>
      </div>
      <p className="mt-2 text-[12px] leading-5 text-gray-500">{hint}</p>
    </div>
  );
}

const getScoreStyle = (score) => {
  if (score <= 300)
    return { bg: "bg-red-100", text: "text-red-700", border: "border-red-400" };
  if (score <= 600)
    return {
      bg: "bg-yellow-100",
      text: "text-yellow-700",
      border: "border-yellow-400",
    };
  return {
    bg: "bg-green-100",
    text: "text-green-700",
    border: "border-green-400",
  };
};

const InfoItem = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2">
    <div className="min-w-0">
      <span className="block text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {label}
      </span>
      <span className="block text-sm font-semibold text-gray-900">
        {value || "-"}
      </span>
    </div>
  </div>
);

const ScrBacenQuery = ({ data }) => {
  return (
    <div className="space-y-10">
      {/* Bloco 1 - Dados Bacen */}
      <section>
        <TitleStripe>Dados BACEN</TitleStripe>

        {/* Destaques principais */}
        <div className="grid grid-cols-3 gap-4 mt-4">
          <HighlightStat
            icon="HiClipboardList"
            label="Quantidade de Operações"
            value={data?.quantidadeOperacoes ?? "-"}
            hint="Total de contratos/linhas de crédito reportados ao SCR no período-base. Pode incluir operações ativas, baixadas e com diferentes modalidades."
          />

          <HighlightStat
            icon="HiBuildingLibrary"
            label="Quantidade de Instituições"
            value={data?.quantidadeInstituicoes ?? "-"}
            hint="Número de instituições financeiras que reportaram operações para este CPF/CNPJ. Indica diversidade de relacionamento bancário."
          />

          <HighlightStat
            icon="HiCalendarDays"
            label="Início do Relacionamento"
            value={data?.dataInicioRelacionamento ?? "-"}
            hint="Primeira ocorrência de relacionamento encontrada no histórico do SCR. Não é a data de abertura do CPF/CNPJ, e pode variar conforme o reporte."
          />
        </div>

        {/* Blocos detalhados */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Coluna 1 - Resumo Geral */}
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500/80 via-sky-400/80 to-emerald-400/80" />
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                  <HiClipboardList className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Resumo Geral
                </h3>
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                SCR
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm">
              <InfoItem
                label="Documento"
                value={formatDocument(data?.documento)}
              />
              <InfoItem label="Tipo de Documento" value={data?.tipoDocumento} />

              {/* REMOVIDOS dos destaques para evitar duplicidade:
            - Quantidade de Operações
            - Quantidade de Instituições
            - Início do Relacionamento
        */}

              <InfoItem
                label="Coobrigação Assumida"
                value={formatToReal(data?.coobrigacaoAssumida)}
              />
              <InfoItem
                label="Coobrigação Recebida"
                value={formatToReal(data?.coobrigacaoRecebida)}
              />
              <InfoItem
                label="Data Base Consultada"
                value={data?.databaseConsultada}
              />
            </div>
          </div>

          {/* Coluna 2 - Indicadores */}
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500/80 via-teal-400/80 to-cyan-400/80" />
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50">
                  <HiBuildingLibrary className="h-4 w-4 text-emerald-600" />
                </div>
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Indicadores de Confiabilidade
                </h3>
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                Qualidade
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3 text-sm">
              <InfoItem
                label="Percentual de Documentos Processados"
                value={
                  data?.percentualDocumentosProcessados != null
                    ? `${data?.percentualDocumentosProcessados}%`
                    : "-"
                }
              />
              <InfoItem
                label="Percentual de Volume Processado"
                value={
                  data?.percentualVolumeProcessado != null
                    ? `${data?.percentualVolumeProcessado}%`
                    : "-"
                }
              />
              <InfoItem
                label="Operações em Discordância"
                value={data?.quantidadeOperacoesDiscordancia}
              />
              <InfoItem
                label="Total Discordância"
                value={formatToReal(data?.responsabilidadeTotalDiscordancia)}
              />
              <InfoItem
                label="Operações Subjudice"
                value={data?.responsabilidadeTotalSubjudice}
              />
              <InfoItem
                label="Total Subjudice"
                value={formatToReal(data?.responsabilidadeTotalSubjudice)}
              />
              <InfoItem
                label="Risco Indireto (Vendor)"
                value={formatToReal(data?.riscoIndiretoVendor)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bloco 2 - Score */}
      <section>
        <TitleStripe className="mb-4">SCR Score</TitleStripe>
        {data?.score ? (
          (() => {
            const score = +data?.score?.PONTUACAO;
            const { bg, text, border } = getScoreStyle(score);
            return (
              <div className="flex flex-row items-center gap-6 p-6 bg-white border border-gray-200 rounded-xl">
                {/* Círculo com destaque numérico */}
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400/40 via-transparent to-emerald-300/40 blur-md" />
                  <div className="absolute inset-1 rounded-full bg-white/70" />
                  <div
                    className={`relative flex items-center justify-center w-32 h-32 rounded-full ${bg} ${border} ${text} border-4 shadow-inner`}
                  >
                    <span className="text-3xl font-extrabold tracking-tight">
                      {score}
                    </span>
                  </div>
                </div>

                {/* Texto explicativo */}
                <div className="flex-1 text-sm text-gray-700 leading-relaxed">
                  <p className="mb-1">
                    <span className="font-semibold text-gray-800">
                      Importante:
                    </span>{" "}
                    a decisão de concessão de crédito é responsabilidade
                    exclusiva do concedente.
                  </p>
                  <p>
                    Os dados apresentados têm caráter informativo e{" "}
                    <strong>
                      não devem ser utilizados como justificativa única
                    </strong>{" "}
                    para aprovação ou recusa.
                  </p>
                </div>
              </div>
            );
          })()
        ) : (
          <div className="text-red-600 font-semibold text-sm mt-3">
            ESTE DOCUMENTO NÃO TEM TRANSAÇÕES NO PERÍODO DA PESQUISA.
          </div>
        )}
      </section>

      {/* Blocos 3+ - Consolidado */}
      {renderConsolidado("Crédito a Vencer", data?.consolidado?.creditoAVencer)}
      {renderConsolidado(
        "Crédito Vencido",
        data?.consolidado?.creditoVencido,
        true
      )}
      {renderConsolidado("Limite de Crédito", data?.consolidado?.limiteCredito)}
      {renderConsolidado("Prejuízos", data?.consolidado?.prejuizo, true)}

      {/* Bloco 7 - Operações Detalhadas */}
      <section>
        <TitleStripe>Operações</TitleStripe>
        {data?.operacoes?.length ? (
          <div className="space-y-6 mt-3">
            <div className="overflow-x-auto border border-gray-200 rounded-xl">
              <Table striped>
                <Table.Head>
                  <Table.HeadCell>Modalidade</Table.HeadCell>
                  <Table.HeadCell className="text-center">Total</Table.HeadCell>
                  <Table.HeadCell className="text-center">Percentual</Table.HeadCell>
                  <Table.HeadCell className="text-center">
                    Variação cambial
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body>
                  {data.operacoes.map((operation, idx) => (
                    <Table.Row
                      key={`${operation.modalidade || "operacao"}-${idx}`}
                    >
                      <Table.Cell>
                        <div className="text-sm font-semibold text-gray-900">
                          {operation.modalidade || "-"}
                        </div>
                        <div className="text-[11px] text-gray-500">
                          {operation.subModalidade || "-"}
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        {operation.total ? formatToReal(operation.total) : "-"}
                      </Table.Cell>
                      <Table.Cell className="text-center">
                        {operation.percentual
                          ? `${operation.percentual}%`
                          : "-"}
                      </Table.Cell>
                      <Table.Cell className="text-center px-4">
                        {operation.variacaoCambial || "-"}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>

            <div className="space-y-5">
              <TitleStripe>
                Operações detalhadas
              </TitleStripe>
              {data.operacoes.map((operation, idx) => (
                <div
                  key={`${operation.modalidade || "operacao-detail"}-${idx}`}
                  className="mt-4"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {operation.modalidade || "-"}
                      </div>
                      <p className="text-[11px] text-gray-500">
                        {operation.subModalidade || "-"}
                      </p>
                    </div>
                    <div className="text-[11px] text-gray-500 text-right flex items-center gap-3">
                      <div>
                        <span className="font-semibold text-gray-900">Total:</span>{" "}
                        {operation.total ? formatToReal(operation.total) : "-"}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">
                          Percentual:
                        </span>{" "}
                        {operation.percentual ? `${operation.percentual}%` : "-"}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-900">
                          Variação cambial:
                        </span>{" "}
                        {operation.variacaoCambial || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 overflow-x-auto">
                    <Table striped>
                      <Table.Head>
                        <Table.HeadCell>Código</Table.HeadCell>
                        <Table.HeadCell>Descrição</Table.HeadCell>
                        <Table.HeadCell className="text-center">
                          Valor
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center">
                          Meses
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center">
                          Percentual
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center">
                          Restritivo
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body>
                        {operation.vencimentos?.map((item, itemIdx) => (
                          <Table.Row
                            key={`${operation.modalidade}-${item.codigo}-${itemIdx}`}
                          >
                            <Table.Cell>{item.codigo || "-"}</Table.Cell>
                            <Table.Cell className="max-w-[20ch]">
                              <span className="text-[12px] text-gray-500">
                                {item.descricao || "-"}
                              </span>
                            </Table.Cell>
                            <Table.Cell className="text-center">
                              {item.valor ? formatToReal(item.valor) : "-"}
                            </Table.Cell>
                            <Table.Cell className="text-center">
                              {item.qtdMeses || "-"}
                            </Table.Cell>
                            <Table.Cell className="text-center">
                              {item.percentual ? `${item.percentual}%` : "-"}
                            </Table.Cell>
                            <Table.Cell className="text-center">
                              <span
                                className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  item.restritivo === "1"
                                    ? "bg-rose-100 text-rose-700"
                                    : "bg-emerald-100 text-emerald-700"
                                }`}
                              >
                                {item.restritivo === "1" ? "Sim" : "Não"}
                              </span>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                        {!operation.vencimentos?.length && (
                          <Table.Row>
                            <Table.Cell colSpan={6} className="text-center">
                              Nenhum vencimento encontrado.
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <NoOcurrence />
        )}
      </section>
    </div>
  );
};

const renderConsolidado = (title, bloco, highlight = false) => (
  <section>
    <TitleStripe>{title}</TitleStripe>
    {bloco?.operacoes?.length > 0 ? (
      <div className="overflow-x-auto border border-gray-200 rounded-xl mt-3">
        <Table striped>
          <Table.Head>
            <Table.HeadCell>Descrição</Table.HeadCell>
            <Table.HeadCell className="text-center">Valor</Table.HeadCell>
            <Table.HeadCell className="text-center">Qtd. Meses</Table.HeadCell>
            <Table.HeadCell className="text-center">Percentual</Table.HeadCell>
          </Table.Head>
          <Table.Body>
            <Table.Row>
              <Table.Cell className="font-bold">Total</Table.Cell>
              <Table.Cell
                className={`text-center font-bold ${
                  highlight ? "text-red-600" : ""
                }`}
              >
                {formatToReal(bloco?.valor)}
              </Table.Cell>
              <Table.Cell className="text-center font-bold">-</Table.Cell>
              <Table.Cell className="text-center font-bold">
                {bloco?.percentual || bloco?.PERCENTUAL}%
              </Table.Cell>
            </Table.Row>
            {bloco.operacoes.map((item, i) => (
              <Table.Row key={i}>
                <Table.Cell>{item.descricao || "-"}</Table.Cell>
                <Table.Cell className="text-center">
                  {item.valor ? formatToReal(item.valor) : "-"}
                </Table.Cell>
                <Table.Cell className="text-center">
                  {item?.qtdMeses || "-"}
                </Table.Cell>
                <Table.Cell className="text-center">
                  {item.percentual ? `${item.percentual}%` : "-"}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    ) : (
      <NoOcurrence />
    )}
  </section>
);

export default ScrBacenQuery;
